"use strict";
// shared-types/src/database/material-categories.ts
// Material Field Taxonomy — parent_field + propagation + scope
Object.defineProperty(exports, "__esModule", { value: true });
exports.CATEGORY_DEFINITIONS = exports.YARN_WEIGHT_DEFAULTS = void 0;
exports.isParentEditableField = isParentEditableField;
exports.isChildEditableField = isChildEditableField;
exports.isInventoryField = isInventoryField;
exports.getParentPropagation = getParentPropagation;
exports.getCategoryConfig = getCategoryConfig;
exports.getSubcategoryConfig = getSubcategoryConfig;
exports.getCategoryFields = getCategoryFields;
exports.getCoreFields = getCoreFields;
exports.getVariationFields = getVariationFields;
exports.getParentEditableFieldConfigs = getParentEditableFieldConfigs;
exports.getParentPropagationRules = getParentPropagationRules;
exports.getExpectedAttributes = getExpectedAttributes;
exports.getResolvedCategoryFields = getResolvedCategoryFields;
exports.getResolvedFieldsByGroup = getResolvedFieldsByGroup;
exports.getPrimaryFieldsResolved = getPrimaryFieldsResolved;
exports.getAdditionalFieldsResolved = getAdditionalFieldsResolved;
exports.getInventoryFieldsResolved = getInventoryFieldsResolved;
exports.getPrimaryFields = getPrimaryFields;
exports.getAdditionalFields = getAdditionalFields;
exports.getInventoryFields = getInventoryFields;
exports.getAllCategories = getAllCategories;
exports.getSubcategories = getSubcategories;
exports.getSuggestedUnits = getSuggestedUnits;
exports.getFallbackCategoryConfig = getFallbackCategoryConfig;
exports.getCategoryConfigSafe = getCategoryConfigSafe;
const productField = (name, label, data_type, propagation, opts) => ({
    name, label, data_type,
    parent_field: true,
    propagation,
    scope: 'product',
    ...opts,
});
const variationField = (name, label, data_type, opts) => ({
    name, label, data_type,
    parent_field: false,
    propagation: 'never',
    scope: 'variation',
    ...opts,
});
const inventoryField = (name, label, data_type, opts) => ({
    name, label, data_type,
    parent_field: false,
    propagation: 'never',
    scope: 'inventory',
    ...opts,
});
// --- Taxonomy helpers ---
function isParentEditableField(field) {
    return field.parent_field;
}
function isChildEditableField(field) {
    if (!field.parent_field)
        return true;
    return field.propagation !== 'always';
}
function isInventoryField(field) {
    return field.scope === 'inventory';
}
function getParentPropagation(field) {
    return field.parent_field ? field.propagation : null;
}
// --- UI resolution helpers ---
const PRIMARY_FIELDS = new Set(['product_name', 'name', 'brand', 'quantity', 'unit']);
const INVENTORY_FIELDS = new Set(['storage_location', 'status', 'purchase_source', 'purchase_date', 'purchase_price', 'notes']);
const getDefaultUiGroup = (name) => {
    if (PRIMARY_FIELDS.has(name))
        return 'primary';
    if (INVENTORY_FIELDS.has(name))
        return 'inventory';
    return 'additional';
};
function resolveUiGroup(field) {
    return field.ui_group ?? getDefaultUiGroup(field.name);
}
function resolveUiComponent(field) {
    if (field.ui_component)
        return field.ui_component;
    if (field.options)
        return 'select';
    if (field.data_type === 'string_array')
        return 'chips';
    if (field.name.endsWith('_date'))
        return 'date';
    if (field.name === 'description' || field.name === 'notes')
        return 'textarea';
    return 'input';
}
const DEFAULT_UI_ORDER = {
    product_name: 10,
    name: 11,
    brand: 12,
    quantity: 13,
    unit: 14,
    storage_location: 20,
    status: 21,
    purchase_source: 22,
    purchase_date: 23,
    purchase_price: 24,
    notes: 25,
};
const DEFAULT_ROW_LAYOUT = {
    quantity: { row: 'qty_unit', col: 0 },
    unit: { row: 'qty_unit', col: 1 },
    width_value: { row: 'width', col: 0 },
    width_unit: { row: 'width', col: 1 },
    length_amount: { row: 'length', col: 0 },
    length_unit: { row: 'length', col: 1 },
    skein_weight_amount: { row: 'skein_weight', col: 0 },
    skein_weight_unit: { row: 'skein_weight', col: 1 },
};
function resolveUiRow(field) {
    return field.ui_row ?? DEFAULT_ROW_LAYOUT[field.name]?.row ?? field.name;
}
function resolveUiCol(field) {
    return field.ui_col ?? DEFAULT_ROW_LAYOUT[field.name]?.col ?? 0;
}
function resolveUiSpan(field) {
    return field.ui_span ?? DEFAULT_ROW_LAYOUT[field.name]?.span ?? 4;
}
function resolveUiOrder(field) {
    return field.ui_order ?? DEFAULT_UI_ORDER[field.name] ?? 999;
}
// --- Color fields helper ---
const COLOR_FAMILIES = [
    'black', 'white', 'gray', 'brown', 'red', 'orange', 'yellow',
    'green', 'blue', 'purple', 'pink', 'metallic', 'multicolor', 'clear', 'natural',
];
function getColorFields(opts) {
    const base = opts?.ui_order_base ?? 50;
    const out = [
        variationField('color', 'Color', 'string', {
            placeholder: 'e.g., navy, teal, "neon green", #1A2B3C',
            ui_group: 'additional',
            ui_order: base,
        }),
        variationField('color_family', 'Color Family', 'string', {
            options: [...COLOR_FAMILIES],
            ui_group: 'additional',
            ui_order: base + 1,
        }),
    ];
    if (opts?.include_color_code) {
        out.push(variationField('color_code', 'Color Code', 'string', {
            placeholder: 'e.g., DMC 310, Pantone 186C, Copic R29',
            ui_group: 'additional',
            ui_order: base + 2,
        }));
    }
    if (opts?.include_print) {
        out.push(variationField('print_or_pattern', opts?.print_label ?? 'Print / Pattern', 'string', {
            placeholder: 'e.g., floral, gingham, stripe, polka dot',
            ui_group: 'additional',
            ui_order: base + 3,
        }));
    }
    return out;
}
// --- Needle/hook sizes (UNCHANGED — do not reduce) ---
const KNITTING_NEEDLE_SIZES = [
    ['1.50', 'US 000'], ['1.75', 'US 00'], ['2.00', 'US 0'], ['2.25', 'US 1'],
    ['2.75', 'US 2'], ['3.00', ''], ['3.125', 'US 3'], ['3.25', 'US 3'],
    ['3.50', 'US 4'], ['3.75', 'US 5'], ['4.00', 'US 6'], ['4.25', 'US 6'],
    ['4.50', 'US 7'], ['5.00', 'US 8'], ['5.25', 'US 9'], ['5.50', 'US 9'],
    ['5.75', 'US 10'], ['6.00', 'US 10'], ['6.50', 'US 10½'], ['7.00', ''],
    ['8.00', 'US 11'], ['9.00', 'US 13'], ['10.00', 'US 15'], ['12.50', 'US 17'],
    ['12.75', 'US 17'], ['15.00', 'US 19'], ['19.00', 'US 35'], ['25.00', 'US 50'],
    ['35.00', 'US 70'],
];
const CROCHET_HOOK_SIZES = [
    ['2.25', 'B-1'], ['2.50', ''], ['2.75', 'C-2'], ['3.125', 'D'],
    ['3.25', 'D-3'], ['3.50', 'E-4'], ['3.75', 'F-5'], ['4.00', 'G-6'],
    ['4.25', 'G'], ['4.50', '7'], ['5.00', 'H-8'], ['5.25', 'I'],
    ['5.50', 'I-9'], ['5.75', 'J'], ['6.00', 'J-10'], ['6.50', 'K-10½'],
    ['7.00', ''], ['8.00', 'L-11'], ['9.00', 'M/N-13'], ['10.00', 'N/P-15'],
    ['11.50', 'P-16'], ['12.00', ''], ['15.00', 'P/Q'], ['15.75', 'Q'],
    ['16.00', 'Q'], ['19.00', 'S'], ['25.00', 'T/U/X'], ['30.00', 'T/X'],
];
const toSizeOptions = (sizes) => sizes.map(([mm, us]) => (us ? `${mm} mm (${us})` : `${mm} mm`));
exports.YARN_WEIGHT_DEFAULTS = {
    '0 - Lace': { yarn_types: 'Fingering, 10-count crochet thread', knitting_gauge: '33–40 sts = 4 in', needle_size_mm: '1.5–2.25 mm', needle_size_us: 'US 000–1', crochet_gauge: '32–42 dc = 4 in', hook_size_mm: '1.4–2.25 mm', hook_size_us: 'Steel 6–8 / B-1' },
    '1 - Super Fine': { yarn_types: 'Sock, Fingering, Baby', knitting_gauge: '27–32 sts = 4 in', needle_size_mm: '2.25–3.25 mm', needle_size_us: 'US 1–3', crochet_gauge: '21–32 sts = 4 in', hook_size_mm: '2.25–3.5 mm', hook_size_us: 'B-1 to E-4' },
    '2 - Fine': { yarn_types: 'Sport, Baby', knitting_gauge: '23–26 sts = 4 in', needle_size_mm: '3.25–3.75 mm', needle_size_us: 'US 3–5', crochet_gauge: '16–20 sts = 4 in', hook_size_mm: '3.5–4.5 mm', hook_size_us: 'E-4 to 7' },
    '3 - Light': { yarn_types: 'DK, Light Worsted', knitting_gauge: '21–24 sts = 4 in', needle_size_mm: '3.75–4.5 mm', needle_size_us: 'US 5–7', crochet_gauge: '12–17 sts = 4 in', hook_size_mm: '4.5–5.5 mm', hook_size_us: '7 to I-9' },
    '4 - Medium': { yarn_types: 'Worsted, Afghan, Aran', knitting_gauge: '16–20 sts = 4 in', needle_size_mm: '4.5–5.5 mm', needle_size_us: 'US 7–9', crochet_gauge: '11–14 sts = 4 in', hook_size_mm: '5.5–6.5 mm', hook_size_us: 'I-9 to K-10½' },
    '5 - Bulky': { yarn_types: 'Chunky, Craft, Rug', knitting_gauge: '12–15 sts = 4 in', needle_size_mm: '5.5–8 mm', needle_size_us: 'US 9–11', crochet_gauge: '8–11 sts = 4 in', hook_size_mm: '6.5–9 mm', hook_size_us: 'K-10½ to M-13' },
    '6 - Super Bulky': { yarn_types: 'Super Bulky, Roving', knitting_gauge: '7–11 sts = 4 in', needle_size_mm: '8–12.75 mm', needle_size_us: 'US 11–17', crochet_gauge: '7–9 sts = 4 in', hook_size_mm: '9–15 mm', hook_size_us: 'M-13 to Q' },
    '7 - Jumbo': { yarn_types: 'Jumbo, Roving', knitting_gauge: '6 sts or fewer = 4 in', needle_size_mm: '12.75 mm+', needle_size_us: 'US 17+', crochet_gauge: '6 sts or fewer = 4 in', hook_size_mm: '15 mm+', hook_size_us: 'Q and larger' },
};
// --- Universal fields (included in every category) ---
function getUniversalFields() {
    return cloneFields([
        productField('product_name', 'Product Name', 'string', 'always', {
            required: true,
            placeholder: 'e.g., Red Heart Super Saver, DMC Embroidery Floss, Siser EasyWeed',
            ui_group: 'primary', ui_order: 10,
        }),
        variationField('name', 'Variation Name', 'string', {
            required: true,
            placeholder: 'e.g., Ocean Mist, 310 (Black), 12x12 (Mat), 5.00 mm (US 8)',
            ui_group: 'primary', ui_order: 11,
        }),
        productField('brand', 'Brand', 'string', 'always', { ui_group: 'primary', ui_order: 12 }),
        productField('description', 'Description', 'string', 'fill_empty', {
            placeholder: 'Generic product overview; variations can add specifics',
            ui_group: 'additional',
        }),
        variationField('sku_or_upc', 'SKU/UPC', 'string', {
            placeholder: 'Barcode or product code',
            ui_group: 'additional',
        }),
        // Inventory fields
        inventoryField('quantity', 'Quantity', 'number', { placeholder: 'How many you have', ui_group: 'primary', ui_order: 13 }),
        inventoryField('unit', 'Unit', 'string', { ui_group: 'primary', ui_order: 14 }),
        inventoryField('storage_location', 'Storage Location', 'string', { placeholder: 'e.g., Shelf A, Bin 3', ui_group: 'inventory', ui_order: 20 }),
        inventoryField('status', 'Status', 'string', { options: ['in_stock', 'running_low', 'out_of_stock', 'wishlist'], ui_group: 'inventory', ui_order: 21 }),
        inventoryField('purchase_source', 'Purchase Source', 'string', { placeholder: 'Store name or website', ui_group: 'inventory', ui_order: 22 }),
        inventoryField('purchase_date', 'Purchase Date', 'string', { placeholder: 'YYYY-MM-DD', ui_group: 'inventory', ui_order: 23 }),
        inventoryField('purchase_price', 'Purchase Price', 'number', { placeholder: 'Amount paid', ui_group: 'inventory', ui_order: 24 }),
        inventoryField('notes', 'Notes', 'string', { placeholder: "Personal notes (e.g., bought for baby blanket, don't use for hats)", ui_group: 'inventory', ui_order: 25 }),
    ]);
}
// --- Category definitions ---
exports.CATEGORY_DEFINITIONS = {
    // 1) FABRIC
    fabric: {
        label: 'Fabric & Textiles',
        icon: '🧵',
        suggested_units: ['yards', 'meters', 'fat_quarters', 'pieces', 'bolts'],
        fields: [
            ...getUniversalFields(),
            productField('fiber_content', 'Fiber Content', 'string', 'always', { placeholder: 'e.g., 100% cotton', ui_group: 'primary', ui_order: 15 }),
            productField('fabric_type', 'Fabric Type', 'string', 'always', { options: ['woven', 'knit', 'nonwoven', 'leather', 'vinyl'], ui_group: 'primary', ui_order: 16 }),
            productField('care', 'Care Instructions', 'string', 'fill_empty', { placeholder: 'e.g., machine wash warm', ui_group: 'additional' }),
            productField('width_value', 'Width', 'number', 'fill_empty', { ui_group: 'additional' }),
            productField('width_unit', 'Width Unit', 'string', 'fill_empty', { options: ['inches', 'cm'], ui_group: 'additional' }),
            productField('weight', 'Weight', 'string', 'fill_empty', { options: ['lightweight', 'medium', 'heavyweight'], ui_group: 'additional' }),
            productField('stretch', 'Stretch', 'string', 'fill_empty', { options: ['none', 'slight', 'moderate', 'high'], ui_group: 'additional' }),
            variationField('pattern_scale', 'Pattern Scale', 'string', { options: ['solid', 'small', 'medium', 'large'], ui_group: 'additional' }),
            ...getColorFields({ include_print: true, print_label: 'Print / Pattern' }),
        ],
        subcategories: {
            quilting_cotton: { label: 'Quilting Cotton', fields: [] },
            cotton_wovens: { label: 'Cotton Wovens', fields: [] },
            apparel_knits: { label: 'Apparel Knits', fields: [] },
            jersey: { label: 'Jersey', fields: [] },
            french_terry: { label: 'French Terry', fields: [] },
            fleece: { label: 'Fleece', fields: [] },
            flannel: { label: 'Flannel', fields: [] },
            felt: { label: 'Felt', fields: [] },
            denim: { label: 'Denim', fields: [] },
            canvas: { label: 'Canvas', fields: [] },
            linen: { label: 'Linen', fields: [] },
            silk: { label: 'Silk', fields: [] },
            satin: { label: 'Satin', fields: [] },
            velvet: { label: 'Velvet', fields: [] },
            chiffon: { label: 'Chiffon', fields: [] },
            upholstery: { label: 'Upholstery', fields: [] },
            outdoor: { label: 'Outdoor', fields: [] },
            leather: { label: 'Leather', fields: [productField('thickness', 'Thickness', 'string', 'fill_empty')] },
            faux_leather: { label: 'Faux Leather', fields: [productField('thickness', 'Thickness', 'string', 'fill_empty')] },
            vinyl: { label: 'Vinyl', fields: [productField('thickness', 'Thickness', 'string', 'fill_empty')] },
        },
    },
    // 2) SEWING NOTIONS
    sewing_notions: {
        label: 'Sewing Notions & Fasteners',
        icon: '📎',
        suggested_units: ['spools', 'pieces', 'pairs', 'sets', 'packages', 'yards', 'meters'],
        fields: [
            ...getUniversalFields(),
            productField('primary_material', 'Primary Material', 'string', 'fill_empty', { placeholder: 'e.g., polyester, cotton, metal', ui_group: 'additional' }),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., 40wt, 1/2 inch, size 14', ui_group: 'additional' }),
            ...getColorFields(),
        ],
        subcategories: {
            thread: { label: 'Thread', fields: [
                    productField('thread_type', 'Thread Type', 'string', 'always', { options: ['all_purpose', 'quilting', 'embroidery', 'topstitch', 'serger'] }),
                    productField('thread_weight', 'Thread Weight', 'string', 'fill_empty', { placeholder: 'e.g., 40wt' }),
                ] },
            bobbins: { label: 'Bobbins', fields: [] },
            needles_hand: { label: 'Hand Needles', fields: [
                    productField('needle_type', 'Needle Type', 'string', 'always', { options: ['sharps', 'embroidery', 'quilting', 'darning', 'leather'] }),
                    variationField('needle_size', 'Needle Size', 'string', { placeholder: 'e.g., 7, 9, 10' }),
                ] },
            needles_machine: { label: 'Machine Needles', fields: [
                    productField('needle_system', 'Needle System', 'string', 'always', { placeholder: 'e.g., 130/705 H' }),
                    variationField('needle_size', 'Needle Size', 'string', { placeholder: 'e.g., 70/10, 90/14' }),
                ] },
            pins_and_clips: { label: 'Pins & Clips', fields: [] },
            buttons: { label: 'Buttons', fields: [] },
            snaps: { label: 'Snaps', fields: [] },
            hook_and_eye: { label: 'Hook & Eye', fields: [] },
            hook_and_loop: { label: 'Hook & Loop Tape', fields: [] },
            zippers: { label: 'Zippers', fields: [
                    productField('zipper_type', 'Zipper Type', 'string', 'always', { options: ['invisible', 'separating', 'coil', 'metal'] }),
                    variationField('length', 'Length', 'string', { placeholder: 'e.g., 9 in' }),
                ] },
            elastic: { label: 'Elastic', fields: [variationField('width', 'Width', 'string', { placeholder: 'e.g., 3/4 in' })] },
            interfacing: { label: 'Interfacing', fields: [productField('interfacing_weight', 'Weight', 'string', 'always', { options: ['lightweight', 'medium', 'heavyweight'] })] },
            stabilizer: { label: 'Stabilizer', fields: [productField('stabilizer_type', 'Stabilizer Type', 'string', 'always', { options: ['cut_away', 'tear_away', 'wash_away', 'heat_away'] })] },
            batting: { label: 'Quilt Batting', fields: [productField('loft', 'Loft', 'string', 'fill_empty', { options: ['low', 'medium', 'high'] })] },
            bias_tape: { label: 'Bias Tape', fields: [] },
            ribbon: { label: 'Ribbon', fields: [variationField('width', 'Width', 'string')] },
            trim: { label: 'Trim', fields: [] },
            lace: { label: 'Lace', fields: [] },
            grommets: { label: 'Grommets', fields: [] },
            rivets: { label: 'Rivets', fields: [] },
            buckles: { label: 'Buckles', fields: [] },
            d_rings: { label: 'D-Rings', fields: [] },
            o_rings: { label: 'O-Rings', fields: [] },
            cord_and_piping: { label: 'Cord & Piping', fields: [] },
        },
    },
    // 3) YARN
    yarn: {
        label: 'Yarn & Fiber',
        icon: '🧶',
        suggested_units: ['skeins', 'balls', 'hanks', 'cones', 'yards', 'meters', 'grams', 'ounces'],
        fields: [
            ...getUniversalFields(),
            productField('model', 'Model / Line', 'string', 'always', { ui_group: 'primary', ui_order: 15 }),
            productField('weight', 'Weight', 'string', 'always', {
                options: ['0 - Lace', '1 - Super Fine', '2 - Fine', '3 - Light', '4 - Medium', '5 - Bulky', '6 - Super Bulky', '7 - Jumbo'],
                required: true, ui_group: 'primary', ui_order: 16,
            }),
            productField('fiber_family', 'Fiber Family', 'string', 'always', {
                options: ['acrylic', 'cotton', 'wool', 'alpaca', 'silk', 'linen', 'bamboo', 'nylon', 'polyester', 'blend'],
                ui_group: 'primary', ui_order: 17,
            }),
            productField('fiber_content', 'Fiber Content', 'string', 'fill_empty', { placeholder: 'e.g., 80% wool / 20% nylon', ui_group: 'additional' }),
            productField('care', 'Care Instructions', 'string', 'fill_empty', { placeholder: 'e.g., machine wash cold', ui_group: 'additional' }),
            productField('length_amount', 'Yardage / Length', 'number', 'fill_empty', { ui_group: 'additional', ui_order: 30 }),
            productField('length_unit', 'Length Unit', 'string', 'fill_empty', { options: ['yards', 'meters'], ui_group: 'additional', ui_order: 31 }),
            productField('skein_weight_amount', 'Skein Weight', 'number', 'fill_empty', { placeholder: 'e.g., 7', ui_group: 'additional', ui_order: 32 }),
            productField('skein_weight_unit', 'Skein Weight Unit', 'string', 'fill_empty', { options: ['oz', 'g'], ui_group: 'additional', ui_order: 33 }),
            productField('knitting_gauge', 'Knitting Gauge', 'string', 'fill_empty', { placeholder: 'e.g., 17 sts / 23 rows = 4 in', ui_group: 'additional', ui_order: 34 }),
            productField('crochet_gauge', 'Crochet Gauge', 'string', 'fill_empty', { placeholder: 'e.g., 12 sc / 15 rows = 4 in', ui_group: 'additional', ui_order: 35 }),
            productField('suggested_needle_size', 'Suggested Needle Size', 'string', 'fill_empty', { placeholder: 'e.g., 5 mm / US 8', ui_group: 'additional', ui_order: 36 }),
            productField('suggested_hook_size', 'Suggested Hook Size', 'string', 'fill_empty', { placeholder: 'e.g., 5.5 mm / US I-9', ui_group: 'additional', ui_order: 37 }),
            ...getColorFields({ ui_order_base: 54 }),
            variationField('dye_lot', 'Dye Lot', 'string', { ui_group: 'additional', ui_order: 60 }),
        ],
        subcategories: {
            superwash_wool: { label: 'Superwash Wool', fields: [] },
            sock_yarn: { label: 'Sock Yarn', fields: [] },
            chenille: { label: 'Chenille', fields: [] },
            boucle: { label: 'Bouclé', fields: [] },
            novelty: { label: 'Novelty', fields: [] },
            crochet_thread: { label: 'Crochet Thread', fields: [] },
            macrame_cord: { label: 'Macramé Cord', fields: [] },
            rug_yarn: { label: 'Rug Yarn', fields: [] },
            tapestry_yarn: { label: 'Tapestry Yarn', fields: [] },
            embroidery_yarn: { label: 'Embroidery Yarn', fields: [] },
        },
    },
    // 4) KNITTING + CROCHET TOOLS
    needles_hooks: {
        label: 'Knitting & Crochet Tools',
        icon: '🪡',
        suggested_units: ['pieces', 'sets', 'pairs'],
        fields: [
            ...getUniversalFields(),
            productField('primary_material', 'Primary Material', 'string', 'always', {
                options: ['metal', 'bamboo', 'wood', 'plastic', 'carbon_fiber', 'ergonomic'],
            }),
        ],
        subcategories: {
            knitting_straight: { label: 'Knitting Needles (Straight)', fields: [
                    variationField('size_mm', 'Size', 'string', { options: toSizeOptions(KNITTING_NEEDLE_SIZES), placeholder: 'Select size' }),
                    variationField('length', 'Length', 'string', { placeholder: 'e.g., 10 in' }),
                ] },
            knitting_circular: { label: 'Knitting Needles (Circular)', fields: [
                    variationField('size_mm', 'Size', 'string', { options: toSizeOptions(KNITTING_NEEDLE_SIZES), placeholder: 'Select size' }),
                    variationField('cable_length', 'Cable Length', 'string', { placeholder: 'e.g., 16 in' }),
                ] },
            knitting_dpn: { label: 'Knitting Needles (Double Pointed)', fields: [
                    variationField('size_mm', 'Size', 'string', { options: toSizeOptions(KNITTING_NEEDLE_SIZES), placeholder: 'Select size' }),
                    variationField('length', 'Length', 'string', { placeholder: 'e.g., 6 in' }),
                    variationField('set_count', 'Set Count', 'number'),
                ] },
            knitting_interchangeable: { label: 'Interchangeable Needles', fields: [
                    variationField('size_mm', 'Size', 'string', { options: toSizeOptions(KNITTING_NEEDLE_SIZES), placeholder: 'Select size' }),
                    variationField('cable_length', 'Cable Length', 'string'),
                ] },
            crochet_hooks: { label: 'Crochet Hooks', fields: [
                    variationField('size_mm', 'Size', 'string', { options: toSizeOptions(CROCHET_HOOK_SIZES), placeholder: 'Select size' }),
                ] },
            tunisian_hooks: { label: 'Tunisian Hooks', fields: [
                    variationField('size_mm', 'Size', 'string', { options: toSizeOptions(CROCHET_HOOK_SIZES), placeholder: 'Select size' }),
                    variationField('cable_length', 'Cable Length', 'string'),
                ] },
            stitch_markers: { label: 'Stitch Markers', fields: [] },
            row_counters: { label: 'Row Counters', fields: [] },
            needle_gauges: { label: 'Needle / Hook Gauges', fields: [] },
            cables: { label: 'Cables & Connectors', fields: [] },
        },
    },
    // 5) NEEDLE ARTS
    needle_arts: {
        label: 'Needle Arts & Embroidery',
        icon: '🧵',
        suggested_units: ['skeins', 'spools', 'pieces', 'packs', 'yards', 'meters'],
        fields: [
            ...getUniversalFields(),
            productField('item_type', 'Type', 'string', 'always', { options: ['floss', 'thread', 'fabric', 'canvas', 'hoop', 'frame', 'kit'] }),
            variationField('size', 'Size / Count', 'string', { placeholder: 'e.g., 14 count, 6 strands, 8 in hoop' }),
            ...getColorFields({ include_color_code: true }),
        ],
        subcategories: {
            embroidery_floss: { label: 'Embroidery Floss', fields: [] },
            pearl_cotton: { label: 'Pearl Cotton', fields: [] },
            embroidery_thread: { label: 'Embroidery Thread', fields: [] },
            aida: { label: 'Aida Cloth', fields: [] },
            evenweave: { label: 'Evenweave', fields: [] },
            linen_cloth: { label: 'Linen Cloth', fields: [] },
            needlepoint_canvas: { label: 'Needlepoint Canvas', fields: [] },
            hoops_and_frames: { label: 'Hoops & Frames', fields: [] },
            stabilizer: { label: 'Stabilizer (Needle Arts)', fields: [] },
            patterns_and_kits: { label: 'Patterns & Kits', fields: [] },
        },
    },
    // 6) PAPER
    paper: {
        label: 'Paper Crafts & Scrapbooking',
        icon: '📄',
        suggested_units: ['sheets', 'packs', 'pads', 'rolls', 'pieces'],
        fields: [
            ...getUniversalFields(),
            productField('weight', 'Weight', 'string', 'fill_empty', { placeholder: 'e.g., 65lb, 110lb' }),
            productField('finish', 'Finish', 'string', 'fill_empty', { options: ['matte', 'glossy', 'satin', 'textured', 'smooth'] }),
            productField('size', 'Sheet Size', 'string', 'fill_empty', { placeholder: 'e.g., 8.5x11, 12x12' }),
            ...getColorFields({ include_print: true }),
        ],
        subcategories: {
            cardstock: { label: 'Cardstock', fields: [] },
            patterned_paper: { label: 'Patterned Paper', fields: [] },
            scrapbook_pads: { label: 'Paper Pads', fields: [] },
            vellum: { label: 'Vellum', fields: [] },
            tissue_paper: { label: 'Tissue Paper', fields: [] },
            envelopes: { label: 'Envelopes', fields: [] },
            journals_planners: { label: 'Journals & Planners', fields: [] },
            stickers: { label: 'Stickers', fields: [] },
            washi_tape: { label: 'Washi Tape', fields: [] },
            stamps: { label: 'Stamps', fields: [] },
            ink_pads: { label: 'Ink Pads', fields: [] },
            embossing: { label: 'Embossing', fields: [] },
            dies: { label: 'Dies', fields: [] },
            die_cuts: { label: 'Die Cuts', fields: [] },
            ephemera: { label: 'Ephemera', fields: [] },
        },
    },
    // 7) ART SUPPLIES
    art_supplies: {
        label: 'Art Supplies (Paint, Ink & Drawing)',
        icon: '🎨',
        suggested_units: ['pieces', 'sets', 'bottles', 'tubes', 'pans', 'pads', 'sheets', 'ounces', 'milliliters'],
        fields: [
            ...getUniversalFields(),
            productField('medium', 'Medium', 'string', 'always', { options: ['acrylic', 'watercolor', 'gouache', 'oil', 'ink', 'marker', 'pencil', 'pastel', 'spray'] }),
            productField('finish', 'Finish', 'string', 'fill_empty', { options: ['matte', 'satin', 'gloss'] }),
            productField('surface', 'Surface', 'string', 'fill_empty', { options: ['paper', 'canvas', 'fabric', 'wood', 'multi'] }),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., 2 oz, 50ml, 9x12 pad' }),
            ...getColorFields({ include_color_code: true }),
        ],
        subcategories: {
            paint_acrylic: { label: 'Acrylic Paint', fields: [] },
            paint_watercolor: { label: 'Watercolor', fields: [] },
            paint_gouache: { label: 'Gouache', fields: [] },
            paint_oil: { label: 'Oil Paint', fields: [] },
            paint_fabric: { label: 'Fabric Paint', fields: [] },
            paint_spray: { label: 'Spray Paint', fields: [] },
            ink_alcohol: { label: 'Alcohol Ink', fields: [] },
            ink_india: { label: 'India Ink', fields: [] },
            markers: { label: 'Markers', fields: [] },
            pens: { label: 'Pens', fields: [] },
            colored_pencils: { label: 'Colored Pencils', fields: [] },
            graphite_charcoal: { label: 'Graphite & Charcoal', fields: [] },
            pastels: { label: 'Pastels', fields: [] },
            brushes: { label: 'Brushes', fields: [] },
            canvas_panels: { label: 'Canvas & Panels', fields: [] },
            sketchbooks_paper: { label: 'Sketchbooks & Paper', fields: [] },
            mediums: { label: 'Mediums & Additives', fields: [] },
        },
    },
    // 8) BEADS + JEWELRY
    beads: {
        label: 'Beads & Jewelry',
        icon: '📿',
        suggested_units: ['pieces', 'strands', 'spools', 'yards', 'meters', 'grams'],
        fields: [
            ...getUniversalFields(),
            productField('primary_material', 'Primary Material', 'string', 'always', { options: ['glass', 'crystal', 'metal', 'acrylic', 'wood', 'stone'] }),
            variationField('finish', 'Finish', 'string', { options: ['matte', 'glossy', 'metallic', 'transparent', 'opaque', 'AB'] }),
            variationField('shape', 'Shape', 'string', { options: ['round', 'bicone', 'seed', 'bugle', 'cube', 'oval', 'teardrop'] }),
            ...getColorFields(),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., 6mm, 11/0' }),
            variationField('hole_size', 'Hole Size', 'string', { placeholder: 'e.g., 1mm' }),
        ],
        subcategories: {
            seed_beads: { label: 'Seed Beads', fields: [] },
            glass_beads: { label: 'Glass Beads', fields: [] },
            gemstone_beads: { label: 'Gemstone Beads', fields: [] },
            metal_beads: { label: 'Metal Beads', fields: [] },
            findings: { label: 'Findings', fields: [] },
            jump_rings: { label: 'Jump Rings', fields: [] },
            clasps: { label: 'Clasps', fields: [] },
            ear_wires: { label: 'Ear Wires', fields: [] },
            headpins: { label: 'Headpins', fields: [] },
            wire: { label: 'Wire', fields: [
                    productField('gauge', 'Gauge', 'string', 'fill_empty'),
                    variationField('temper', 'Temper', 'string', { options: ['dead_soft', 'half_hard', 'hard'] }),
                ] },
            chain: { label: 'Chain', fields: [] },
            cord: { label: 'Cord', fields: [] },
            elastic_cord: { label: 'Elastic Cord', fields: [] },
            leather_cord: { label: 'Leather Cord', fields: [] },
            crimp_beads: { label: 'Crimps', fields: [] },
        },
    },
    // 9) CUTTING MACHINES
    cutting_machines: {
        label: 'Cutting Machines & Vinyl',
        icon: '🪄',
        suggested_units: ['rolls', 'sheets', 'pieces', 'sets'],
        fields: [
            ...getUniversalFields(),
            productField('item_type', 'Type', 'string', 'always', { options: ['adhesive_vinyl', 'htv', 'transfer_tape', 'mat', 'blade', 'tool', 'accessory'] }),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., 12x12, 12x24' }),
            ...getColorFields(),
        ],
        subcategories: {
            vinyl_adhesive: { label: 'Adhesive Vinyl', fields: [] },
            vinyl_htv: { label: 'Heat Transfer Vinyl (HTV)', fields: [productField('carrier', 'Carrier', 'string', 'always', { options: ['warm_peel', 'cold_peel'] })] },
            vinyl_specialty: { label: 'Specialty Vinyl', fields: [] },
            transfer_tape: { label: 'Transfer Tape', fields: [] },
            weeding_tools: { label: 'Weeding Tools', fields: [] },
            cutting_mats: { label: 'Cutting Mats (Machines)', fields: [] },
            blades: { label: 'Blades', fields: [] },
            pens: { label: 'Pens & Markers (Machines)', fields: [] },
            foil_transfer: { label: 'Foil Transfer', fields: [] },
            smart_materials: { label: 'Smart Materials', fields: [] },
            machine_accessories: { label: 'Machine Accessories', fields: [] },
        },
    },
    // 10) ADHESIVES
    adhesives: {
        label: 'Adhesives, Tape & Finishes',
        icon: '🧴',
        suggested_units: ['bottles', 'tubes', 'sticks', 'rolls', 'ounces', 'milliliters'],
        fields: [
            ...getUniversalFields(),
            productField('item_type', 'Type', 'string', 'always', { options: ['permanent', 'removable', 'repositionable', 'washable'] }),
            productField('application', 'Application', 'string', 'always', { options: ['paper', 'fabric', 'vinyl', 'wood', 'multi'] }),
            productField('form', 'Form', 'string', 'always', { options: ['liquid', 'stick', 'tape', 'spray', 'hot_glue'] }),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., 4 oz, 50ml' }),
        ],
        subcategories: {
            glue_stick: { label: 'Glue Stick', fields: [] },
            pva_glue: { label: 'PVA Glue', fields: [] },
            tacky_glue: { label: 'Tacky Glue', fields: [] },
            super_glue: { label: 'Super Glue', fields: [] },
            fabric_glue: { label: 'Fabric Glue', fields: [] },
            spray_adhesive: { label: 'Spray Adhesive', fields: [] },
            hot_glue_sticks: { label: 'Hot Glue Sticks', fields: [] },
            double_sided_tape: { label: 'Double Sided Tape', fields: [] },
            masking_tape: { label: 'Masking Tape', fields: [] },
            painter_tape: { label: 'Painter Tape', fields: [] },
            mod_podge: { label: 'Decoupage Medium', fields: [] },
            varnish: { label: 'Varnish', fields: [] },
            sealant: { label: 'Sealant', fields: [] },
        },
    },
    // 11) CLAY + RESIN
    clay_resin: {
        label: 'Clay, Resin & Molds',
        icon: '🧱',
        suggested_units: ['blocks', 'pounds', 'ounces', 'kits', 'pieces'],
        fields: [
            ...getUniversalFields(),
            productField('item_type', 'Type', 'string', 'always', { options: ['polymer', 'air_dry', 'ceramic', 'epoxy_resin', 'uv_resin'] }),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., 2 oz, 1 lb' }),
            ...getColorFields(),
        ],
        subcategories: {
            polymer_clay: { label: 'Polymer Clay', fields: [] },
            air_dry_clay: { label: 'Air Dry Clay', fields: [] },
            ceramic_clay: { label: 'Ceramic Clay', fields: [] },
            epoxy_resin: { label: 'Epoxy Resin', fields: [] },
            uv_resin: { label: 'UV Resin', fields: [] },
            molds: { label: 'Molds', fields: [] },
            pigments: { label: 'Pigments', fields: [] },
            mica_powder: { label: 'Mica Powder', fields: [] },
            inclusions: { label: 'Inclusions', fields: [] },
            sculpting_tools: { label: 'Sculpting Tools', fields: [] },
            armature_wire: { label: 'Armature Wire', fields: [] },
            glaze: { label: 'Glaze', fields: [] },
            sealant: { label: 'Sealant (Resin/Clay)', fields: [] },
        },
    },
    // 12) WOOD + DIY
    wood_diy: {
        label: 'Wood & DIY',
        icon: '🪵',
        suggested_units: ['pieces', 'packs', 'feet', 'yards', 'meters'],
        fields: [
            ...getUniversalFields(),
            productField('primary_material', 'Primary Material', 'string', 'always', { options: ['pine', 'oak', 'birch', 'mdf', 'plywood', 'balsa'] }),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., 1x2x8, 1/4 inch thick' }),
            variationField('finish', 'Finish', 'string', { options: ['unfinished', 'sanded', 'stained', 'painted'] }),
        ],
        subcategories: {
            wood_blanks: { label: 'Wood Blanks', fields: [] },
            unfinished_wood: { label: 'Unfinished Wood', fields: [] },
            dowels: { label: 'Dowels', fields: [] },
            craft_sticks: { label: 'Craft Sticks', fields: [] },
            stencils: { label: 'Stencils', fields: [] },
            hardware_small: { label: 'Small Hardware', fields: [] },
            picture_hangers: { label: 'Picture Hangers', fields: [] },
            mosaic_tiles: { label: 'Mosaic Tiles', fields: [] },
        },
    },
    // 13) FLORAL + DECOR
    floral_decor: {
        label: 'Floral, Wreaths & Seasonal Decor',
        icon: '🌸',
        suggested_units: ['stems', 'bunches', 'pieces', 'rolls'],
        fields: [
            ...getUniversalFields(),
            productField('item_type', 'Type', 'string', 'always', { options: ['artificial', 'dried', 'foam', 'wire', 'decor'] }),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., 12 in stem, 16 in wreath' }),
            ...getColorFields(),
        ],
        subcategories: {
            artificial_flowers: { label: 'Artificial Flowers', fields: [] },
            floral_stems: { label: 'Floral Stems', fields: [] },
            greenery: { label: 'Greenery', fields: [] },
            wreath_forms: { label: 'Wreath Forms', fields: [] },
            floral_foam: { label: 'Floral Foam', fields: [] },
            floral_wire: { label: 'Floral Wire', fields: [] },
            floral_tape: { label: 'Floral Tape', fields: [] },
            picks_sprays: { label: 'Picks & Sprays', fields: [] },
            ornaments: { label: 'Ornaments', fields: [] },
            seasonal_decor: { label: 'Seasonal Decor', fields: [] },
        },
    },
    // 14) STUFFING + DOLLMAKING
    stuffing_dollmaking: {
        label: 'Stuffing & Dollmaking',
        icon: '🧸',
        suggested_units: ['bags', 'pounds', 'ounces', 'pieces', 'sets', 'pairs'],
        fields: [
            ...getUniversalFields(),
            productField('primary_material', 'Primary Material', 'string', 'always', { options: ['polyester', 'cotton', 'wool', 'kapok', 'foam', 'pellets'] }),
            variationField('weight', 'Weight / Size', 'string', { placeholder: 'e.g., 12 oz bag' }),
            variationField('density', 'Density', 'string', { options: ['light', 'medium', 'firm'] }),
        ],
        subcategories: {
            polyfill: { label: 'Polyfill', fields: [] },
            batting: { label: 'Batting', fields: [] },
            foam: { label: 'Foam', fields: [] },
            pellets: { label: 'Pellets', fields: [] },
            pillow_forms: { label: 'Pillow Forms', fields: [] },
            safety_eyes: { label: 'Safety Eyes', fields: [] },
            safety_noses: { label: 'Safety Noses', fields: [] },
            doll_joints: { label: 'Doll Joints', fields: [] },
            armature: { label: 'Armature', fields: [] },
        },
    },
    // 15) KIDS
    kids_crafts: {
        label: 'Kids Crafts & Classroom',
        icon: '🧒',
        suggested_units: ['packs', 'pieces', 'sets'],
        fields: [
            ...getUniversalFields(),
            productField('age_range', 'Age Range', 'string', 'always', { options: ['3+', '5+', '8+', '12+', 'adult'] }),
            variationField('size', 'Size', 'string'),
            ...getColorFields(),
        ],
        subcategories: {
            pom_poms: { label: 'Pom Poms', fields: [] },
            googly_eyes: { label: 'Googly Eyes', fields: [] },
            pipe_cleaners: { label: 'Pipe Cleaners', fields: [] },
            foam_sheets: { label: 'Foam Sheets', fields: [] },
            craft_sticks: { label: 'Craft Sticks', fields: [] },
            washable_paint: { label: 'Washable Paint', fields: [] },
            glitter: { label: 'Glitter', fields: [] },
            beads_kids: { label: 'Kids Beads', fields: [] },
            safety_scissors: { label: 'Safety Scissors', fields: [] },
        },
    },
    // 16) STORAGE
    storage: {
        label: 'Storage & Organization',
        icon: '🗂️',
        suggested_units: ['pieces', 'sets'],
        fields: [
            ...getUniversalFields(),
            productField('primary_material', 'Primary Material', 'string', 'always', { options: ['plastic', 'fabric', 'cardboard', 'metal', 'wood'] }),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., 12x8x4 in' }),
            variationField('capacity', 'Capacity', 'string', { placeholder: 'e.g., 64 compartments' }),
        ],
        subcategories: {
            bins: { label: 'Bins', fields: [] },
            totes: { label: 'Totes', fields: [] },
            jars: { label: 'Jars', fields: [] },
            drawers: { label: 'Drawers', fields: [] },
            dividers: { label: 'Dividers', fields: [] },
            bobbin_boxes: { label: 'Bobbin Boxes', fields: [] },
            project_bags: { label: 'Project Bags', fields: [] },
            label_maker: { label: 'Label Maker', fields: [] },
            organizers: { label: 'Organizers', fields: [] },
        },
    },
    // 17) EMBELLISHMENTS
    embellishments: {
        label: 'Embellishments & Bling',
        icon: '✨',
        suggested_units: ['pieces', 'packages', 'grams'],
        fields: [
            ...getUniversalFields(),
            productField('primary_material', 'Primary Material', 'string', 'fill_empty'),
            variationField('finish', 'Finish', 'string', { options: ['matte', 'glossy', 'metallic', 'iridescent'] }),
            variationField('size', 'Size', 'string'),
            ...getColorFields(),
        ],
        subcategories: {
            rhinestones: { label: 'Rhinestones', fields: [] },
            sequins: { label: 'Sequins', fields: [] },
            patches: { label: 'Patches', fields: [] },
            applique: { label: 'Appliqué', fields: [] },
            charms: { label: 'Charms', fields: [] },
        },
    },
    // 18) BLANKS + BASES
    blanks_bases: {
        label: 'Blanks & Bases',
        icon: '👕',
        suggested_units: ['pieces', 'packs', 'sets'],
        fields: [
            ...getUniversalFields(),
            productField('blank_type', 'Blank Type', 'string', 'always', {
                options: ['tshirt', 'tote', 'hat', 'mug', 'tumbler', 'pillow_cover', 'sign', 'ornament', 'canvas_blank'],
                ui_group: 'primary',
            }),
            productField('primary_material', 'Primary Material', 'string', 'fill_empty', { placeholder: 'e.g., cotton, polyester, ceramic, wood' }),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., Adult L, 15oz mug, 12x12 canvas' }),
            ...getColorFields(),
        ],
        subcategories: {
            tshirts: { label: 'T-Shirts', fields: [] },
            totes: { label: 'Tote Bags', fields: [] },
            hats: { label: 'Hats', fields: [] },
            mugs: { label: 'Mugs', fields: [] },
            tumblers: { label: 'Tumblers', fields: [] },
            pillow_covers: { label: 'Pillow Covers', fields: [] },
            signs: { label: 'Signs', fields: [] },
            ornaments_blanks: { label: 'Ornament Blanks', fields: [] },
            canvas_blank: { label: 'Canvas / Painting Blanks', fields: [] },
        },
    },
    // 19) TOOLS
    tools: {
        label: 'Tools & Measuring',
        icon: '✂️',
        suggested_units: ['pieces', 'sets', 'pairs'],
        fields: [
            ...getUniversalFields(),
            productField('primary_material', 'Primary Material', 'string', 'fill_empty', { options: ['metal', 'plastic', 'bamboo', 'wood', 'carbon_fiber'] }),
            variationField('size', 'Size', 'string', { placeholder: 'e.g., 8 in, 24cm' }),
        ],
        subcategories: {
            scissors_shears: { label: 'Scissors & Shears', fields: [] },
            rotary_cutter: { label: 'Rotary Cutter', fields: [] },
            cutting_mat: { label: 'Cutting Mat', fields: [] },
            craft_knife: { label: 'Craft Knife', fields: [] },
            replacement_blades: { label: 'Replacement Blades', fields: [] },
            rulers: { label: 'Rulers', fields: [] },
            tape_measure: { label: 'Tape Measure', fields: [] },
            french_curve: { label: 'French Curve', fields: [] },
            seam_ripper: { label: 'Seam Ripper', fields: [] },
            awl: { label: 'Awl', fields: [] },
            punches: { label: 'Punches', fields: [] },
            pliers: { label: 'Pliers', fields: [] },
            wire_cutters: { label: 'Wire Cutters', fields: [] },
            glue_gun: { label: 'Glue Gun', fields: [] },
            heat_tool: { label: 'Heat Tool', fields: [] },
            marking_tools: { label: 'Marking Tools', fields: [] },
        },
    },
};
// --- Read helpers ---
function getCategoryConfig(category) {
    return exports.CATEGORY_DEFINITIONS[category];
}
function getSubcategoryConfig(category, subcategory) {
    return getCategoryConfigSafe(category).subcategories?.[subcategory];
}
function getCategoryFields(category, subcategory) {
    const cat = getCategoryConfigSafe(category);
    let base = populateUnitOptions(cloneFields(cat.fields), cat.suggested_units);
    if (subcategory) {
        const sub = cat.subcategories?.[subcategory];
        if (sub)
            base.push(...populateUnitOptions(cloneFields(sub.fields), cat.suggested_units));
    }
    return base;
}
/** Returns names of all parent-owned fields (replaces old is_core filter) */
function getCoreFields(category, subcategory) {
    return getCategoryFields(category, subcategory)
        .filter((f) => f.parent_field)
        .map((f) => f.name);
}
/** Returns names of all child-editable fields (replaces old is_variation filter) */
function getVariationFields(category, subcategory) {
    return getCategoryFields(category, subcategory)
        .filter((f) => isChildEditableField(f))
        .map((f) => f.name);
}
/** Returns field configs for all parent-editable fields */
function getParentEditableFieldConfigs(category, subcategory) {
    return getCategoryFields(category, subcategory).filter((f) => f.parent_field);
}
/** Returns propagation rules for all parent fields: { fieldName: propagation } */
function getParentPropagationRules(category, subcategory) {
    const rules = {};
    for (const f of getCategoryFields(category, subcategory)) {
        if (f.parent_field)
            rules[f.name] = f.propagation;
    }
    return rules;
}
function getExpectedAttributes(category, subcategory) {
    return getCategoryFields(category, subcategory).map((f) => f.name);
}
// --- UI resolvers ---
function resolveField(field) {
    return {
        ...field,
        ui_group: resolveUiGroup(field),
        ui_order: resolveUiOrder(field),
        ui_component: resolveUiComponent(field),
        ui_row: resolveUiRow(field),
        ui_col: resolveUiCol(field),
        ui_span: resolveUiSpan(field),
    };
}
function getResolvedCategoryFields(category, subcategory) {
    return getCategoryFields(category, subcategory).map(resolveField);
}
function getResolvedFieldsByGroup(category, subcategory) {
    const fields = getResolvedCategoryFields(category, subcategory);
    return {
        primary: fields.filter((f) => f.ui_group === 'primary').sort((a, b) => a.ui_order - b.ui_order || a.label.localeCompare(b.label)),
        additional: fields.filter((f) => f.ui_group === 'additional').sort((a, b) => a.ui_order - b.ui_order || a.label.localeCompare(b.label)),
        inventory: fields.filter((f) => f.ui_group === 'inventory').sort((a, b) => a.ui_order - b.ui_order || a.label.localeCompare(b.label)),
    };
}
function getPrimaryFieldsResolved(category, subcategory) {
    return getResolvedFieldsByGroup(category, subcategory).primary;
}
function getAdditionalFieldsResolved(category, subcategory) {
    return getResolvedFieldsByGroup(category, subcategory).additional;
}
function getInventoryFieldsResolved(category, subcategory) {
    return getResolvedFieldsByGroup(category, subcategory).inventory;
}
function getPrimaryFields(category, subcategory) {
    return getCategoryFields(category, subcategory)
        .filter((f) => resolveUiGroup(f) === 'primary')
        .sort((a, b) => resolveUiOrder(a) - resolveUiOrder(b) || a.label.localeCompare(b.label));
}
function getAdditionalFields(category, subcategory) {
    return getCategoryFields(category, subcategory)
        .filter((f) => resolveUiGroup(f) === 'additional')
        .sort((a, b) => resolveUiOrder(a) - resolveUiOrder(b) || a.label.localeCompare(b.label));
}
function getInventoryFields(category, subcategory) {
    return getCategoryFields(category, subcategory)
        .filter((f) => resolveUiGroup(f) === 'inventory')
        .sort((a, b) => resolveUiOrder(a) - resolveUiOrder(b) || a.label.localeCompare(b.label));
}
function getAllCategories() {
    return Object.entries(exports.CATEGORY_DEFINITIONS)
        .map(([value, def]) => ({ value, label: def.label, icon: def.icon }))
        .sort((a, b) => a.label.localeCompare(b.label));
}
function getSubcategories(category) {
    const cat = getCategoryConfigSafe(category);
    if (!cat.subcategories)
        return [];
    return Object.entries(cat.subcategories)
        .map(([value, sub]) => ({ value, label: sub.label }))
        .sort((a, b) => a.label.localeCompare(b.label));
}
function getSuggestedUnits(category) {
    return getCategoryConfigSafe(category).suggested_units;
}
// --- Internal helpers ---
function cloneFields(fields) {
    return fields.map((f) => ({ ...f, ...(f.options ? { options: [...f.options] } : {}) }));
}
function populateUnitOptions(fields, suggestedUnits) {
    return fields.map((field) => {
        if (field.name === 'unit')
            return { ...field, options: suggestedUnits };
        return field;
    });
}
function getFallbackCategoryConfig(categoryName) {
    return {
        label: categoryName,
        icon: '📦',
        suggested_units: ['pieces'],
        fields: getUniversalFields(),
    };
}
const fallbackCache = {};
function getCategoryConfigSafe(category) {
    const known = exports.CATEGORY_DEFINITIONS[category];
    if (known)
        return known;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    if (!fallbackCache[category])
        fallbackCache[category] = getFallbackCategoryConfig(category);
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return fallbackCache[category];
}
